import { pageHandler } from "@/lib/pages-handler";
import { getRecipePage, getRecipeConfig } from "@/dato-api/recipe";
import { buildHrefLangsList, getPortfolioSlug } from "@/lib/functions";
import { getReferencingPortfolioByProductId } from "@/dato-api/awards";
import { fetchComponentRelations } from "@/lib/api";
import HeroRecipe, { HeroRecipeCocktail } from "@/organisms/Hero/HeroRecipe";
import RecipeOverview from "@/organisms/RecipeOverview";
import IngredientsBlock from "@/organisms/IngredientsBlock";
import DirectionsBlock from "@/organisms/DirectionsBlock";
import RecipeGallery from "@/organisms/RecipeGallery";
import RecipePairingColumn, {
  RecipePairingMobileCta,
} from "@/components/Molecules/RecipePairingColumn";
import Blocks from "@/components/Organisms";

import { useRouter } from "next/router";

const recipeContainerClass =
  "container mx-auto 2xl:max-w-[1360px]";

const recipeContentGridClass = `grid lg:grid-cols-2 ${recipeContainerClass} pt-10 md:pt-12 lg:pt-16 xl:pt-20`;

export default function Recipe({ data, relations }) {
  const { locale } = useRouter();
  const portfolioSlug = getPortfolioSlug(data?.portfolioConf?.[0], locale);

  if (data.layoutType == "cocktail") {
    return (
      <div
        style={{
          backgroundColor:
            data.pageConfig?.brandRecipe?.backgroundColor?.color?.hex,
        }}
      >
        <HeroRecipeCocktail
          config={data.pageConfig?.brandRecipe}
          title={data.name}
          description={data?.alternativeHeadline}
          image={data?.featuredImage}
          label={data?.layoutType}
        />
        <div className={recipeContentGridClass}>
          <RecipePairingColumn
            pairing={data.pairing}
            config={data.pageConfig?.brandRecipe}
            portfolioSlug={portfolioSlug}
            locale={locale}
          />
          <div>
            <RecipeOverview
              layout={data.layoutType}
              data={data}
              config={data.pageConfig?.brandRecipe}
            />
            <IngredientsBlock
              data={data}
              config={data.pageConfig?.brandRecipe}
            />
            <DirectionsBlock
              data={data}
              layout={data.layoutType}
              config={data.pageConfig?.brandRecipe}
            />
            <RecipeGallery data={data} config={data.pageConfig?.brandRecipe} />
          </div>
        </div>
        <div className={`${recipeContainerClass} max-lg:block lg:hidden`}>
          <RecipePairingMobileCta
            pairing={data.pairing}
            config={data.pageConfig?.brandRecipe}
            portfolioSlug={portfolioSlug}
            locale={locale}
            layoutType={data.layoutType}
          />
        </div>
        <Blocks
          sections={data.pageConfig?.brandRecipe.relatedSections}
          relations={relations}
        ></Blocks>
      </div>
    );
  } else {
    return (
      <>
        <HeroRecipe
          config={data.pageConfig?.brandRecipe}
          title={data.name}
          description={data?.alternativeHeadline}
          image={data?.featuredImage}
          overlay={data.addOverlay}
        />

        <div className={recipeContentGridClass}>
          <RecipePairingColumn
            pairing={data.pairing}
            config={data.pageConfig?.brandRecipe}
            portfolioSlug={portfolioSlug}
            locale={locale}
          />
          <div>
            <RecipeOverview data={data} config={data.pageConfig?.brandRecipe} />
            <IngredientsBlock
              data={data}
              config={data.pageConfig?.brandRecipe}
            />
            <DirectionsBlock
              data={data}
              config={data.pageConfig?.brandRecipe}
            />
            <RecipeGallery data={data} config={data.pageConfig?.brandRecipe} />
          </div>
        </div>
        <div className={`${recipeContainerClass} max-lg:block lg:hidden`}>
          <RecipePairingMobileCta
            pairing={data.pairing}
            config={data.pageConfig?.brandRecipe}
            portfolioSlug={portfolioSlug}
            locale={locale}
            layoutType={data.layoutType}
          />
        </div>
        <Blocks
          sections={data.pageConfig?.brandRecipe.relatedSections}
          relations={relations}
        ></Blocks>
      </>
    );
  }
}

export async function _getStaticProps(
  context,
  pageType,
  serverSideTranslations,
  siteConfig
) {
  const preview = context.preview !== undefined ? context.preview : false;

  const currentSlug =
    context.params.slug.length > 1
      ? context.params.slug[1]
      : context.params.slug[0];

  const result = await getRecipePage(
    currentSlug,
    context.locale,
    preview,
    siteConfig.siteConfiguration.id
  );

  const pageConfig = await getRecipeConfig(
    context.locale,
    preview,
    siteConfig.siteConfiguration.id
  );

  const pageData = await pageHandler(
    context,
    serverSideTranslations,
    result,
    "recipe"
  );

  // Get portfolio configuration
  const { productPortfolio } = await getReferencingPortfolioByProductId(
    context.locale,
    preview,
    pageData.data.pairing.id,
    siteConfig.siteConfiguration.id
  );

  const { portfolioConf } = productPortfolio;

  const _allSlugLocales = pageData.data?._allSlugLocales;
  const hrefLangs = buildHrefLangsList(
    _allSlugLocales,
    pageData.data?.__typename
  );

  const relations = await fetchComponentRelations(
    { sections: pageConfig?.brandRecipe.relatedSections },
    context.locale,
    context.preview
  );

  return {
    props: {
      siteConfig: siteConfig.siteConfiguration,
      ...pageData.sst,
      data: {
        ...pageData.data,
        pageConfig,
        hrefLangs,
        portfolioConf,
      },
      preview: pageData.preview,
      relations: relations,
      pageType,
    },
  };
}
